/* ------------------------------------------------------------------
   fjör reviews page — live review feed

   Reads Klaviyo Reviews' public read-only widget endpoint directly:
     https://fast.a.klaviyo.com/reviews/api/client_reviews/<product_id>/

   Deliberately does NOT load Klaviyo's widget.js. That script is gated
   behind analytics consent in snippets/fjor-consent.liquid, so on a
   dedicated reviews page it would leave the page empty for anyone who
   declines. This is a plain cross-origin GET with credentials omitted:
   no cookies are sent or set, nothing is tracked, so it runs for every
   visitor regardless of their consent choice.

   Progressive enhancement only. The section already renders a curated
   set of reviews in Liquid; if this fails for any reason those stay put.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var API = 'https://fast.a.klaviyo.com/reviews/api/client_reviews/';
  var PAGE = 50; // the endpoint caps a single response at 50
  var MAX = 200; // safety stop, per product

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function starsMarkup(rating) {
    var pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    var path =
      'M6.362 1.265a.62.62 0 0 1 1.154 0l1.391 3.589a.62.62 0 0 0 .543.394l3.843.214a.62.62 0 0 1 ' +
      '.357 1.098l-2.983 2.432a.62.62 0 0 0-.207.638l.984 3.721a.62.62 0 0 1-.934.679l-3.234-2.086a.62.62 ' +
      '0 0 0-.671 0l-3.234 2.086a.62.62 0 0 1-.934-.679l.984-3.721a.62.62 0 0 0-.207-.638L.229 6.56a.62.62 ' +
      '0 0 1 .357-1.098l3.843-.214a.62.62 0 0 0 .543-.394l1.391-3.589z';
    var row = '';
    for (var i = 0; i < 5; i++) {
      row += '<svg viewBox="0 0 14 15" fill="none" aria-hidden="true"><path d="' + path + '"/></svg>';
    }
    var wrap = el('span', 'fjor-stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', Number(rating).toFixed(1) + ' out of 5 stars');
    wrap.innerHTML =
      '<span class="fjor-stars__row fjor-stars__track">' + row + '</span>' +
      '<span class="fjor-stars__fill" style="width:' + pct + '%"><span class="fjor-stars__row">' + row + '</span></span>';
    return wrap;
  }

  function verifiedBadge() {
    var s = el('span', 'fjor-rv-verified');
    s.innerHTML =
      '<svg viewBox="0 0 13 13" fill="none" aria-hidden="true">' +
      '<rect width="12.6" height="12.6" rx="6.3" fill="currentColor"/>' +
      '<path d="M9.475 3.981L4.567 8.889 2.931 7.253" stroke="#fff" stroke-width=".9"/></svg>';
    s.appendChild(document.createTextNode('Verified buyer'));
    return s;
  }

  function formatDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function url(productId, companyId, offset) {
    return (
      API + encodeURIComponent(productId) + '/?product_id=' + encodeURIComponent(productId) +
      '&company_id=' + encodeURIComponent(companyId) +
      '&limit=' + PAGE + '&offset=' + offset +
      '&sort=3&filter=&type=reviews&media=false&kl_review_uuid=' +
      '&preferred_country=GB&tz=' + encodeURIComponent('Europe/London')
    );
  }

  function getJSON(u) {
    return fetch(u, { credentials: 'omit', mode: 'cors' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // Pull every published review for one product, following has_more.
  function loadProduct(product, companyId) {
    var out = [];
    var summary = null;

    function page(offset) {
      return getJSON(url(product.id, companyId, offset)).then(function (data) {
        if (!summary && data && data.product) summary = data.product;
        var list = (data && data.reviews) || [];
        list.forEach(function (r) {
          out.push({
            id: r.id,
            rating: Number(r.rating) || 0,
            title: r.title || '',
            content: r.content || '',
            author: r.author || 'fjör customer',
            created: r.created_at || '',
            verified: r.verified === true,
            productId: String(product.id),
            productTitle: product.title,
            productUrl: product.url,
            replyAuthor: r.public_reply_author || '',
            replyContent: r.public_reply_content || ''
          });
        });
        if (data && data.has_more && out.length < MAX) return page(offset + PAGE);
        return null;
      });
    }

    return page(0).then(function () {
      return { reviews: out, summary: summary };
    });
  }

  function buildCard(r) {
    var card = el('article', 'fjor-rv-card');

    var meta = el('div', 'fjor-rv-card__meta');
    meta.appendChild(starsMarkup(r.rating));
    if (r.verified) meta.appendChild(verifiedBadge());
    meta.appendChild(el('span', 'fjor-rv-card__author', r.author));
    if (r.created) meta.appendChild(el('span', 'fjor-rv-card__date', formatDate(r.created)));
    if (r.productTitle) {
      var link = el('a', 'fjor-rv-card__product', r.productTitle);
      link.href = r.productUrl || '#';
      meta.appendChild(link);
    }
    card.appendChild(meta);

    var main = el('div', 'fjor-rv-card__main');
    if (r.title) main.appendChild(el('h3', 'fjor-rv-card__title', r.title));
    if (r.content) main.appendChild(el('div', 'fjor-rv-card__body', r.content));

    if (r.replyContent) {
      var reply = el('div', 'fjor-rv-card__reply');
      reply.appendChild(el('b', null, (r.replyAuthor || 'fj\u00f6r') + ': '));
      reply.appendChild(document.createTextNode(r.replyContent));
      main.appendChild(reply);
    }
    card.appendChild(main);

    return card;
  }

  // --- "Read more" ---------------------------------------------------------
  // Collapsing is applied here rather than in CSS so that a review is only
  // truncated when it genuinely runs past the line limit, and so that with
  // JavaScript off every review renders in full.
  var clampSeq = 0;

  function applyClamp(card, lines) {
    var body = card.querySelector('.fjor-rv-card__body');
    if (!body) return;

    var btn = card.querySelector('.fjor-rv-card__more');
    var wasExpanded = btn ? btn.getAttribute('aria-expanded') === 'true' : false;

    // Measure at full height — scrollHeight of a clamped box is not the
    // untruncated height in every engine.
    card.classList.remove('is-clamped');

    var lineHeight = parseFloat(getComputedStyle(body).lineHeight);
    if (!lineHeight) return;

    if (body.scrollHeight <= lineHeight * lines + 2) {
      if (btn) btn.parentNode.removeChild(btn);
      return;
    }

    if (!btn) {
      if (!body.id) body.id = 'fjor-rv-body-' + ++clampSeq;
      btn = el('button', 'fjor-rv-card__more', 'Read more');
      btn.type = 'button';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', body.id);
      btn.addEventListener('click', function () {
        var collapsed = card.classList.toggle('is-clamped');
        btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        btn.textContent = collapsed ? 'Read more' : 'Show less';
      });
      body.parentNode.insertBefore(btn, body.nextSibling);
    }

    if (!wasExpanded) card.classList.add('is-clamped');
  }

  var fontsHooked = false;

  function clampAll(root, lines) {
    if (!lines) return;

    // Run synchronously: reading scrollHeight forces the layout we need, and
    // requestAnimationFrame is paused outright in a hidden or zero-size tab.
    root.querySelectorAll('.fjor-rv-card').forEach(function (c) { applyClamp(c, lines); });

    // Web fonts can land after this and change the line height, which changes
    // which reviews are long enough to need collapsing. Re-measure once.
    if (!fontsHooked && document.fonts && document.fonts.ready) {
      fontsHooked = true;
      document.fonts.ready.then(function () { clampAll(root, lines); });
    }
  }

  function init(root) {
    var configNode = root.querySelector('[data-fjor-reviews-config]');
    if (!configNode || typeof fetch !== 'function' || typeof Promise !== 'function') return;

    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (e) {
      return;
    }
    if (!config.companyId || !config.products || !config.products.length) return;

    var grid = root.querySelector('[data-fjor-reviews-grid]');
    var moreBtn = root.querySelector('[data-fjor-reviews-more]');
    var emptyNode = root.querySelector('[data-fjor-reviews-empty]');
    var noteNode = root.querySelector('[data-fjor-reviews-note]');
    var sortSel = root.querySelector('[data-fjor-reviews-sort]');
    var filterBtns = root.querySelectorAll('[data-fjor-reviews-filter]');
    var pageSize = Number(config.pageSize) || 9;
    var clampLines = Number(config.clampLines) || 0;

    var all = [];
    var filter = 'all';
    var sort = 'recent';
    var shown = pageSize;

    function visible() {
      var list = filter === 'all' ? all.slice() : all.filter(function (r) { return r.productId === filter; });
      list.sort(function (a, b) {
        if (sort === 'highest') return b.rating - a.rating || (b.created < a.created ? -1 : 1);
        if (sort === 'lowest') return a.rating - b.rating || (b.created < a.created ? -1 : 1);
        return a.created < b.created ? 1 : -1;
      });
      return list;
    }

    function render() {
      var list = visible();
      grid.innerHTML = '';
      list.slice(0, shown).forEach(function (r) { grid.appendChild(buildCard(r)); });

      if (emptyNode) emptyNode.hidden = list.length !== 0;
      if (noteNode) noteNode.hidden = list.length === 0;
      if (moreBtn) {
        moreBtn.hidden = list.length <= shown;
        moreBtn.textContent = 'Show more reviews (' + (list.length - shown) + ' left)';
      }
      if (noteNode) {
        noteNode.textContent =
          'Showing ' + Math.min(shown, list.length) + ' of ' + list.length +
          ' published reviews, pulled live from Klaviyo.';
      }

      clampAll(root, clampLines);
    }

    filterBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filter = btn.getAttribute('data-fjor-reviews-filter');
        shown = pageSize;
        filterBtns.forEach(function (b) { b.classList.toggle('active', b === btn); });
        render();
      });
    });

    if (sortSel) {
      sortSel.addEventListener('change', function () {
        sort = sortSel.value;
        shown = pageSize;
        render();
      });
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        shown += pageSize;
        render();
      });
    }

    function load() {
      root.classList.add('is-loading');
      Promise.all(
        config.products.map(function (p) {
          return loadProduct(p, config.companyId).catch(function () { return null; });
        })
      ).then(function (results) {
        var ok = results.filter(Boolean);
        ok.forEach(function (res) { all = all.concat(res.reviews); });

        root.classList.remove('is-loading');
        if (!all.length) return; // keep the Liquid-rendered curated reviews

        // Swap the curated seed out for the live feed.
        root.setAttribute('data-fjor-reviews-live', 'true');

        // Drop filter chips for products the feed returned nothing for —
        // a product's review metafield can be ahead of what Klaviyo publishes.
        filterBtns.forEach(function (btn) {
          var id = btn.getAttribute('data-fjor-reviews-filter');
          if (id === 'all') return;
          var any = all.some(function (r) { return r.productId === id; });
          if (!any) btn.hidden = true;
        });
        if (root.querySelector('[data-fjor-reviews-controls]')) {
          root.querySelector('[data-fjor-reviews-controls]').hidden = false;
        }
        render();

        // Hand the real totals + distribution to the hero panel.
        var total = 0;
        var histogram = [0, 0, 0, 0, 0];
        ok.forEach(function (res) {
          var s = res.summary;
          if (!s) return;
          total += Number(s.review_count) || 0;
          (s.rating_histogram || []).forEach(function (n, i) { histogram[i] += Number(n) || 0; });
        });
        var sum = 0;
        var counted = 0;
        histogram.forEach(function (n, i) { sum += n * (i + 1); counted += n; });
        var withReviews = ok.filter(function (res) {
          return res.summary && Number(res.summary.review_count) > 0;
        }).length;

        if (counted) {
          document.dispatchEvent(new CustomEvent('fjor:reviews:summary', {
            detail: {
              total: total || counted,
              counted: counted,
              products: withReviews,
              histogram: histogram,
              average: sum / counted
            }
          }));
        }
      }).catch(function () {
        root.classList.remove('is-loading');
      });
    }

    // Load when the wall comes into view, but never let that be the only
    // trigger: an IntersectionObserver never fires in a zero-height viewport
    // (headless renderers, some in-app browsers), which would leave the page
    // stuck on the curated seed. Fall back to a timer.
    var started = false;
    function startOnce() {
      if (started) return;
      started = true;
      load();
    }

    // The curated seed is on the page already — collapse it now so the list
    // does not reflow when the live feed replaces it.
    clampAll(root, clampLines);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { clampAll(root, clampLines); }, 200);
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          io.disconnect();
          startOnce();
        }
      }, { rootMargin: '400px' });
      io.observe(root);
      setTimeout(startOnce, 2500);
    } else {
      startOnce();
    }
  }

  function boot() {
    document.querySelectorAll('[data-fjor-reviews-wall]').forEach(init);

    // Hero distribution bars, filled from whatever the wall fetched.
    document.addEventListener('fjor:reviews:summary', function (ev) {
      var d = ev.detail;

      // Keep the hero headline honest: show what the feed actually returned,
      // which can be lower than the sum of the review-count metafields.
      if (d.total) {
        document.querySelectorAll('[data-fjor-total]').forEach(function (n) {
          n.textContent = String(d.total);
        });
      }
      if (d.products) {
        document.querySelectorAll('[data-fjor-product-count]').forEach(function (n) {
          n.textContent = String(d.products);
        });
      }

      document.querySelectorAll('[data-fjor-dist]').forEach(function (box) {
        var counted = d.counted || 0;
        if (!counted) return;
        for (var star = 5; star >= 1; star--) {
          var n = d.histogram[star - 1] || 0;
          var bar = box.querySelector('[data-fjor-dist-bar="' + star + '"]');
          var cnt = box.querySelector('[data-fjor-dist-count="' + star + '"]');
          if (bar) bar.style.width = Math.round((n / counted) * 100) + '%';
          if (cnt) cnt.textContent = String(n);
        }
        box.classList.add('is-ready');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
